/* eslint-disable max-lines-per-function */
import { formatTimeUptoNow } from '../../helper';
import { handleNotificationOnClick } from '../../helper/notification_helper';
import { useNavigate } from 'react-router';
import { AppDispatch, RootState } from '../../redux/store';
import { Avatar, Badge, Box, Button, CircularProgress, Divider, IconButton, Menu, MenuItem, Popover, Typography } from '@mui/material';
import { CalendarMonthOutlined, EmailOutlined, LeaderboardOutlined, MoreVertRounded, NotificationsOutlined, PersonOutlined, PhoneOutlined } from '@mui/icons-material';
import { fetchAllNotificaitonsThunk, markReadUnreadNotificaitonThunk } from '../../redux/thunks/notification';
import { NotificaitonObject, NotificationReadUnreadParams } from '../../params/redux/notification';
import React, { useEffect, useState } from 'react';
import { resetNotificationState, toggleNotificationReadState } from '../../redux/slices/notification';
import { useDispatch, useSelector } from 'react-redux';

const NotificationPopper: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [loadingNotifId, setLoadingNotifId] = useState<number | null>(null);
    const { currentUser, isSales } = useSelector((state: RootState) => state.app);
    const [menuState, setMenuState] = useState<{ anchorEl: HTMLElement | null; selectedNotifId: number | null }>({
        anchorEl: null,
        selectedNotifId: null
    });
    const { notifications } = useSelector((state: RootState) => state.notification);
    const firstTenNotifications = notifications.slice(0, 10);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        if (loadingNotifId !== null) return;
        setAnchorEl(null);
    };

    const handleMenuClick = (event: React.MouseEvent<HTMLButtonElement>, notifId: number) => {
        setMenuState({ anchorEl: event.currentTarget, selectedNotifId: notifId });
    };

    const handleMenuClose = () => {
        setMenuState({ anchorEl: null, selectedNotifId: null });
    };
    const unreadCount = notifications.filter((notif: NotificaitonObject) => notif.is_read === 0).length;
    const markAllAsRead = async () => {
        const originalNotifications: NotificaitonObject[] = JSON.parse(JSON.stringify(notifications));
        try {
            originalNotifications.forEach((n) => {
                dispatch(toggleNotificationReadState({
                    notification_id: n.id,
                    is_read: 1
                }));
            });
            handleMenuClose();
            const data: NotificationReadUnreadParams = {
                notification_id: null,
                is_all: 1,
                is_read: 1
            };
            await dispatch(markReadUnreadNotificaitonThunk(data)).unwrap();
        } catch (e) {
            dispatch(resetNotificationState(originalNotifications));
        }
    };

    const open = Boolean(anchorEl);
    const popperId = open ? 'notification-popper' : '';

    useEffect(() => {
        const fetchNotificaions = async () => {
            try {
                await dispatch(fetchAllNotificaitonsThunk()).unwrap();
            } catch (e) {
                //
            }
        };
        fetchNotificaions();
        const interval = setInterval(() => {
            // fetch Notifications after 30 seconds
            fetchNotificaions();
        }, 30000);
        return () => {
            clearInterval(interval);
        };
    }, []);

    const selectedNotificationHandler = async (notification: NotificaitonObject) => {
        const originalNotifications: NotificaitonObject[] = JSON.parse(JSON.stringify(notifications));
        try {
            dispatch(toggleNotificationReadState({
                notification_id: notification.id,
                is_read: notification.is_read === 1 ? 0 : 1
            }));
            handleMenuClose();
            const data: NotificationReadUnreadParams = {
                notification_id: notification.id,
                is_all: 0,
                is_read: notification.is_read === 1 ? 0 : 1
            };
            await dispatch(markReadUnreadNotificaitonThunk(data)).unwrap();
        } catch (e) {
            dispatch(resetNotificationState(originalNotifications));
        }
    };
    const onNotificationClick = async (notif: NotificaitonObject) => {
        if (loadingNotifId !== null) return;
        // start loading
        setLoadingNotifId(notif.id);
        try {
            // your existing helper does all the dispatches + navigate + close
            await handleNotificationOnClick(
                notif,
                dispatch,
                navigate,
                handleClose,
                currentUser,
                isSales
            );
        } catch (e) {
            console.error('notification click failed', e);
        } finally {
            // stop loading
            setLoadingNotifId(null);
        }
    };

    return (
        <Box>
            {/* Notification Bell with Badge */}
            <IconButton onClick={handleClick} color="inherit">
                <Badge badgeContent={unreadCount} color="primary">
                    <NotificationsOutlined />
                </Badge>
            </IconButton>

            {/* Notification Popper */}
            <Popover
                id={popperId}
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right'
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right'
                }}
                sx={{ mt: 1 }}
            >
                <Box width={350} p={2}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle1" fontWeight="bold">Your Notifications ({notifications.length})</Typography>
                        <Button
                            variant="text"
                            color="primary"
                            sx={{ cursor: 'pointer' }}
                            onClick={markAllAsRead}
                            disabled={loadingNotifId !== null}
                        >
                            Mark all as read
                        </Button>
                    </Box>
                    <Divider />

                    {/* Notifications List */}
                    <Box maxHeight={600} overflow="auto" display="flex" flexDirection="column" gap={1}>
                        {notifications.length > 0
                            ? (
                                firstTenNotifications.map((notif: NotificaitonObject) => (
                                    <Box key={notif.id} display="flex" justifyContent="space-between" alignItems="center" p={1} bgcolor={notif.is_read === 1 ? 'transparent' : '#f5f5f5'}>
                                        <Box display="flex" gap={1} alignItems="center" sx={{
                                            cursor: notif.type === 'general' ? 'initial' : 'pointer',
                                            position: 'relative'
                                        }} onClick={() => onNotificationClick(notif)}>
                                            <Box>
                                                <Avatar sx={{ bgcolor: 'lightgray' }} >
                                                    {notif.type === 'lead'
                                                        ? <LeaderboardOutlined />
                                                        : notif.type === 'sms'
                                                            ? <PhoneOutlined />
                                                            : notif.type === 'email'
                                                                ? <EmailOutlined />
                                                                : notif.type === 'meeting_reminder'
                                                                    ? <CalendarMonthOutlined />
                                                                    : <PersonOutlined />}</Avatar>
                                            </Box>
                                            <Box flexGrow={1}>
                                                <Typography variant="subtitle2" fontWeight="bold">{notif.description}</Typography>
                                                {notif.created_at && formatTimeUptoNow(notif.created_at)}
                                            </Box>
                                        </Box>
                                        {
                                            loadingNotifId === notif.id
                                                ? <CircularProgress size={20} sx={{ ml: 1 }} />
                                                : (
                                                    <IconButton disabled={loadingNotifId !== null} onClick={(e) => handleMenuClick(e, notif.id)}>
                                                        <MoreVertRounded />
                                                    </IconButton>
                                                )
                                        }
                                        <Menu anchorEl={menuState.anchorEl} open={menuState.selectedNotifId === notif.id} onClose={handleMenuClose}>
                                            <MenuItem onClick={() => selectedNotificationHandler(notif)}>Mark as {notif.is_read === 1 ? 'unread' : 'read'}</MenuItem>
                                        </Menu>
                                    </Box>
                                ))
                            )
                            : (
                                <Typography variant="body2" sx={{ textAlign: 'center', mt: 2 }}>
                                    No notifications
                                </Typography>
                            )}
                    </Box>
                    <Divider sx={{ mt: 1 }} />
                    <Button fullWidth sx={{ mt: 1 }} color="primary" disabled={loadingNotifId !== null} onClick={() => {
                        navigate('/app/notification');
                        setAnchorEl(null);
                    }}>
                        View all notifications
                    </Button>
                </Box>
            </Popover>
        </Box>
    );
};

export default NotificationPopper;
